<template lang="pug">
aiq-dialog.question-settings-modal(
    :title="$t(`${baseTranslationPath}.question_settings_title`)"
    :model-value="modelValue"
    :show-close="true"
    :close-on-click-modal="false"
    width="480px"
    @update:model-value="close"
)
    .q-form(v-if="question")
        .q-row
            label {{ $t(`${baseTranslationPath}.prompt`) }} *
            aiq-input(
                v-model="form.prompt"
                type="textarea"
                :rows="2"
                :placeholder="$t(`${baseTranslationPath}.prompt_ph`)"
            )
        .q-row
            label {{ $t(`${baseTranslationPath}.answer_type`) }} *
            aiq-select(v-model="form.answer_type" style="width: 100%" filterable)
                aiq-option(v-for="t in answerTypes" :key="t" :label="t" :value="t")
            .q-hint(v-if="form.answer_type === 'account_number'") {{ $t(`${baseTranslationPath}.answer_type_account_number_note`) }}
        .q-row
            label {{ $t(`${baseTranslationPath}.interaction_class`) }} *
            aiq-select(v-model="form.interaction_class" style="width: 100%" filterable)
                aiq-option(v-for="c in interactionClasses" :key="c" :label="c" :value="c")
            .q-hint {{ $t(`${baseTranslationPath}.interaction_class_hint`) }}
        .q-row
            label {{ $t(`${baseTranslationPath}.group_type`) }}
            aiq-select(v-model="form.group_type" style="width: 100%" clearable filterable)
                aiq-option(v-for="g in groupTypes" :key="g" :label="g" :value="g")
        .q-row.inline
            aiq-switch(v-model="form.required")
            span {{ $t(`${baseTranslationPath}.required`) }}
        .q-row
            label {{ $t(`${baseTranslationPath}.target_fields`) }} *
            aiq-select(
                v-model="form.target_field_ids"
                style="width: 100%"
                multiple
                filterable
                :placeholder="$t(`${baseTranslationPath}.target_fields_ph`)"
            )
                aiq-option(
                    v-for="f in assignableFields"
                    :key="f.field_key"
                    :label="fieldLabel(f)"
                    :value="f.field_key"
                )
        .actions
            aiq-button.delete-btn(type="danger" plain :loading="deleting" @click="onDelete") {{ $t(`${baseTranslationPath}.delete_question`) }}
            .actions-right
                aiq-button(@click="close") {{ $t('global.cancel') }}
                aiq-button(type="primary" :loading="saving" :disabled="!canSubmit" @click="submit") {{ $t('global.save') }}
</template>
<script>
import get from 'lodash/get';
import isEqual from 'lodash/isEqual';

import { fieldOptionLabel } from './fieldUtil';

const ANSWER_TYPES = ['text', 'checkbox', 'radio', 'date', 'currency', 'phone', 'email',
  'account_number', 'routing_number', 'signature', 'initials', 'table_cell', 'computed',
  'internal_only', 'table'];
const INTERACTION_CLASSES = ['simple_input', 'long_text', 'boolean_choice', 'single_choice',
  'multi_choice', 'signature', 'computed', 'internal_only', 'repeating_row', 'repeating_group',
  'composite_group', 'conditional_detail'];
const GROUP_TYPES = ['radio_group', 'checkbox_set', 'table', 'address', 'date_split', 'other'];

export default {
  name: 'QuestionSettingsDialog',
  props: {
    modelValue: {
      type: Boolean,
      default: false,
    },
    formId: {
      type: [String, Number],
      default: null,
    },
    // the selected form_questions row being edited.
    question: {
      type: Object,
      default: null,
    },
    // all form_fields rows for the form.
    fields: {
      type: Array,
      default: () => [],
    },
  },
  emits: ['update:modelValue', 'updated', 'deleted'],
  data() {
    return {
      baseTranslationPath: 'iq_tools_tab.forms_tab.manage',
      answerTypes: ANSWER_TYPES,
      interactionClasses: INTERACTION_CLASSES,
      groupTypes: GROUP_TYPES,
      form: this.emptyForm(),
      saving: false,
      deleting: false,
      suppressAutoType: false,
    };
  },
  computed: {
    // Every field is selectable: assigning one that belongs to another question
    // reassigns it (the backend strips it from its previous owner).
    assignableFields() {
      return this.fields;
    },
    // field keys currently linked to this question, in position order.
    currentTargetKeys() {
      const questionId = get(this.question, 'id');
      return this.fields
        .filter(f => get(f, 'question_ref') === questionId)
        .sort((a, b) => get(a, 'question_position', 0) - get(b, 'question_position', 0))
        .map(f => f.field_key);
    },
    canSubmit() {
      return !!(this.form.prompt && this.form.prompt.trim())
        && !!this.form.answer_type
        && !!this.form.interaction_class
        && this.form.target_field_ids.length > 0;
    },
  },
  watch: {
    // re-seed the form whenever the dialog opens or the selected question changes.
    modelValue(open) {
      if (open) {
        this.seed();
      }
    },
    question() {
      if (this.modelValue) {
        this.seed();
      }
    },
    // Picking target fields auto-sets the answer type from the first field, so the
    // question reflects its primary field without waiting for a save. Suppressed
    // during seeding so opening an existing question keeps its stored answer type.
    'form.target_field_ids'(next) {
      if (this.suppressAutoType) {
        return;
      }
      const firstKey = Array.isArray(next) ? next[0] : null;
      if (!firstKey) {
        return;
      }
      const type = get(this.fields.find(f => get(f, 'field_key') === firstKey), 'field_type');
      if (type && this.answerTypes.includes(type)) {
        this.form.answer_type = type;
      }
    },
  },
  methods: {
    emptyForm() {
      return {
        prompt: '',
        answer_type: 'text',
        interaction_class: 'simple_input',
        group_type: null,
        required: false,
        target_field_ids: [],
      };
    },
    seed() {
      this.suppressAutoType = true;
      this.form = {
        prompt: get(this.question, 'prompt', '') || '',
        answer_type: get(this.question, 'answer_type') || 'text',
        interaction_class: get(this.question, 'interaction_class') || 'simple_input',
        group_type: get(this.question, 'group_type') || null,
        required: Boolean(get(this.question, 'required')),
        target_field_ids: [...this.currentTargetKeys],
      };
      // Let the target_field_ids watcher settle before re-enabling auto-derive,
      // so seeding an existing question doesn't overwrite its stored answer type.
      this.$nextTick(() => {
        this.suppressAutoType = false;
      });
    },
    fieldLabel: fieldOptionLabel,
    close() {
      this.$emit('update:modelValue', false);
    },
    // Only send fields that actually changed, so a prompt tweak doesn't reshuffle
    // target fields and vice versa.
    buildChanges() {
      const body = {};
      const prompt = this.form.prompt.trim();
      if (prompt !== (get(this.question, 'prompt', '') || '')) {
        body.prompt = prompt;
      }
      if (this.form.answer_type !== get(this.question, 'answer_type')) {
        body.answer_type = this.form.answer_type;
      }
      if (this.form.interaction_class !== get(this.question, 'interaction_class')) {
        body.interaction_class = this.form.interaction_class;
      }
      if ((this.form.group_type || null) !== (get(this.question, 'group_type') || null)) {
        body.group_type = this.form.group_type || null;
      }
      if (this.form.required !== Boolean(get(this.question, 'required'))) {
        body.required = this.form.required;
      }
      if (!isEqual(this.form.target_field_ids, this.currentTargetKeys)) {
        body.target_field_ids = this.form.target_field_ids;
      }
      return body;
    },
    async submit() {
      if (!this.canSubmit || !this.formId || !this.question) {
        return;
      }
      const body = this.buildChanges();
      if (!Object.keys(body).length) {
        this.close();
        return;
      }
      this.saving = true;
      try {
        await this.$store.dispatch('agentForms/updateFormQuestion', {
          formId: this.formId,
          questionId: this.question.id,
          ...body,
        });
        this.$aiq.notify.success(this.$t(`${this.baseTranslationPath}.question_updated`));
        this.$emit('updated', this.question.id);
        this.close();
      } catch (e) {
        this.$aiq.notify.error(
          `${this.$t(`${this.baseTranslationPath}.question_update_error`)} (${get(e, 'response.data.message', get(e, 'message', ''))})`,
        );
      } finally {
        this.saving = false;
      }
    },
    async onDelete() {
      if (!this.formId || !this.question) {
        return;
      }
      const name = get(this.question, 'prompt') || get(this.question, 'question_key');
      const confirmed = await this.$aiq.confirm(
        this.$t(`${this.baseTranslationPath}.delete_question_title`),
        this.$t(`${this.baseTranslationPath}.delete_question_confirm`, { name }),
      ).catch(() => false);
      if (!confirmed) {
        return;
      }
      this.deleting = true;
      try {
        await this.$store.dispatch('agentForms/deleteFormQuestion', {
          formId: this.formId,
          questionId: this.question.id,
        });
        this.$aiq.notify.success(this.$t(`${this.baseTranslationPath}.question_deleted`));
        this.$emit('deleted', this.question.id);
        this.close();
      } catch (e) {
        this.$aiq.notify.error(
          `${this.$t(`${this.baseTranslationPath}.question_delete_error`)} (${get(e, 'response.data.message', get(e, 'message', ''))})`,
        );
      } finally {
        this.deleting = false;
      }
    },
  },
};
</script>
<style lang="scss" scoped>
@import 'src/styles/aiq-variables.scss';

.q-form {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.q-row {
  display: flex;
  flex-direction: column;
  gap: 4px;

  label {
    font-size: 12px;
    font-weight: 600;
  }

  &.inline {
    flex-direction: row;
    align-items: center;
    gap: 8px;
  }
}

.q-hint {
  font-size: 11px;
  color: $aiq-color-text-secondary;
}

.actions {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  margin-top: 4px;
}

.actions-right {
  display: flex;
  gap: 8px;
}
</style>
